import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatSubscriptionStatus, isSubscriptionActive } from '../../utils/subscription';
import './UserPowerLocationList.css';

// 발전량 단위 자동 변환 함수 (kW 기준, 소수점 앞 3자리까지 표시 후 다음 단위로 변환)
const formatPowerGeneration = (valueInKw) => {
  if (!valueInKw || valueInKw === 0) {
    return {
      value: '0',
      unit: 'kW'
    };
  }
  
  // 정수 부분의 자리수 계산 (소수점 제외)
  const integerPart = Math.floor(Math.abs(valueInKw));
  const digitCount = integerPart.toString().length;
  
  // 6자리수(999,999) 이하 → kW로 표시 (천 단위 구분자 포함)
  if (digitCount <= 6) {
    return {
      value: Math.round(valueInKw).toLocaleString(),
      unit: 'kW'
    };
  }
  
  // 7자리 이상 → 다음 단위로 변환 (소수점 3자리까지)
  // 변환된 값의 정수 부분이 3자리를 넘으면 다음 단위로
  if (digitCount > 15) {
    // 16자리 이상 → PW (페타와트)
    const pwValue = valueInKw / 1000000000000000;
    const pwIntegerPart = Math.floor(Math.abs(pwValue));
    if (pwIntegerPart.toString().length > 3) {
      return {
        value: pwValue.toFixed(3),
        unit: 'PW'
      };
    }
    return {
      value: pwValue.toFixed(3),
      unit: 'PW'
    };
  } else if (digitCount > 12) {
    // 13자리 이상 → TW (테라와트)
    const twValue = valueInKw / 1000000000000;
    const twIntegerPart = Math.floor(Math.abs(twValue));
    if (twIntegerPart.toString().length > 3) {
      return formatPowerGeneration(valueInKw); // 재귀 호출로 다음 단위로
    }
    return {
      value: twValue.toFixed(3),
      unit: 'TW'
    };
  } else if (digitCount > 9) {
    // 10자리 이상 → GW (기가와트)
    const gwValue = valueInKw / 1000000000;
    const gwIntegerPart = Math.floor(Math.abs(gwValue));
    if (gwIntegerPart.toString().length > 3) {
      return formatPowerGeneration(valueInKw); // 재귀 호출로 다음 단위로
    }
    return {
      value: gwValue.toFixed(3),
      unit: 'GW'
    };
  } else {
    // 7-9자리 → MW (메가와트)
    const mwValue = valueInKw / 1000000;
    const mwIntegerPart = Math.floor(Math.abs(mwValue));
    if (mwIntegerPart.toString().length > 3) {
      return formatPowerGeneration(valueInKw); // 재귀 호출로 다음 단위로
    }
    return {
      value: mwValue.toFixed(3),
      unit: 'MW'
    };
  }
};

const UserPowerLocationList = ({
  powerLocations,
  selectedPowerLocation,
  onPowerLocationSelect,
  getStatusColor,
  getStatusText,
  getSubscriptionForPlant = () => null,
  isLoading = false,
  hasMore = false,
  onLoadMore = null
}) => {
  const navigate = useNavigate();

  const handleCardClick = (powerLocation, subscriptionActive) => {
    if (!subscriptionActive) return;
    onPowerLocationSelect(powerLocation);
  };

  // 더블클릭 핸들러 - 발전소 상세 페이지로 이동
  const handleDoubleClick = (e, powerLocation, subscriptionActive) => {
    e.stopPropagation();
    if (!subscriptionActive || !powerLocation?.id) return;
    navigate(`/power-plant/${powerLocation.id}`);
  };

  if (powerLocations.length === 0) {
    return (
      <div className="user-power-location-list">
        <div className="list-header">
          <h3>내 발전소 목록</h3>
          <span className="power-location-count">0개</span>
        </div>
        <div className="empty-state">
          <div className="empty-icon">🏭</div>
          <p>연결된 발전소가 없습니다</p>
          <small>관리자에게 발전소 연결을 요청하세요</small>
        </div>
      </div>
    );
  }

  return (
    <div className="user-power-location-list">
      <div className="list-header">
        <h3>내 발전소 목록</h3>
        <span className="power-location-count">{powerLocations.length}개</span>
      </div>
      
      <div className="power-location-cards">
        {powerLocations.map(powerLocation => {
          const subscription = getSubscriptionForPlant(powerLocation.id);
          const subscriptionActive = isSubscriptionActive(subscription);
          const subscriptionStatus = formatSubscriptionStatus(subscription);

          return (
          <div
            key={powerLocation.id}
            className={`power-location-card ${selectedPowerLocation?.id === powerLocation.id ? 'selected' : ''} ${subscriptionActive ? '' : 'power-location-card--expired'}`}
            onClick={() => handleCardClick(powerLocation, subscriptionActive)}
            onDoubleClick={(e) => handleDoubleClick(e, powerLocation, subscriptionActive)}
            title={subscriptionActive ? '더블클릭하여 발전소 상세 페이지로 이동' : '이용기간이 종료된 발전소입니다'}
          >
            <div className="power-location-header">
              <div className="power-location-name">
                {powerLocation.name}
                {subscriptionActive && (
                  <span className="double-click-icon" title="더블클릭하여 상세 페이지로 이동">⚡</span>
                )}
              </div>
              <div className="power-location-header-badges">
                <span className={`subscription-badge ${subscriptionStatus.className}`}>
                  {subscriptionStatus.label}
                </span>
                {subscriptionActive && (
                  <div
                    className="power-location-status"
                    style={{ backgroundColor: getStatusColor(powerLocation.status) }}
                  >
                    {getStatusText(powerLocation.status)}
                  </div>
                )}
              </div>
            </div>

            {subscriptionActive ? (
            <>
            <div className={`power-location-info ${powerLocation.environmentSensor.total === 0 ? 'centered' : ''}`}>
              <div className="info-row">
                <span className="info-label">현재 발전량</span>
                <span className="info-value power-value">
                  {typeof powerLocation.powerGeneration === 'number' ? powerLocation.powerGeneration.toFixed(3) : powerLocation.powerGeneration} kW
                </span>
              </div>
              
              <div className="info-row">
                <span className="info-label">오늘 발전량</span>
                <span className="info-value daily-value">
                  {(() => {
                    // MWh를 kW로 변환 (1 MWh = 1,000 kW)
                    const dailyKw = typeof powerLocation.dailyGeneration === 'number' ? powerLocation.dailyGeneration * 1000 : 0;
                    const formatted = formatPowerGeneration(dailyKw);
                    return `${formatted.value} ${formatted.unit}`;
                  })()}
                </span>
              </div>
              
              <div className="info-row">
                <span className="info-label">총 누적 발전량</span>
                <span className="info-value total-value">
                  {(() => {
                    // MWh를 kW로 변환 (1 MWh = 1,000 kW)
                    const totalKw = typeof powerLocation.totalGeneration === 'number' ? powerLocation.totalGeneration * 1000 : 0;
                    const formatted = formatPowerGeneration(totalKw);
                    return `${formatted.value} ${formatted.unit}`;
                  })()}
                </span>
              </div>
              
              <div className="info-row">
                <span className="info-label">발전 효율</span>
                <span className="info-value efficiency-value">
                  {typeof powerLocation.efficiency === 'number' ? powerLocation.efficiency.toFixed(2) : powerLocation.efficiency}%
                </span>
              </div>
            </div>
            
            {powerLocation.environmentSensor.total > 0 && (
              <div className="power-location-info-second">
                <div className="info-row">
                  <span className="info-label">경사 일사량</span>
                  <span className="info-value irradiance-value">
                    {(powerLocation.inclinedIrradiance != null && powerLocation.inclinedIrradiance !== undefined) ? `${powerLocation.inclinedIrradiance} W/m²` : '-'}
                  </span>
                </div>
                
                <div className="info-row">
                  <span className="info-label">수평 일사량</span>
                  <span className="info-value irradiance-value">
                    {(powerLocation.horizontalIrradiance != null && powerLocation.horizontalIrradiance !== undefined) ? `${powerLocation.horizontalIrradiance} W/m²` : '-'}
                  </span>
                </div>
                
                <div className="info-row">
                  <span className="info-label">외기 온도</span>
                  <span className="info-value temperature-value">
                    {(powerLocation.ambientTemperature != null && powerLocation.ambientTemperature !== undefined) ? `${powerLocation.ambientTemperature}°C` : '-'}
                  </span>
                </div>
                
                <div className="info-row">
                  <span className="info-label">표면 온도</span>
                  <span className="info-value temperature-value">
                    {(powerLocation.surfaceTemperature != null && powerLocation.surfaceTemperature !== undefined) ? `${powerLocation.surfaceTemperature}°C` : '-'}
                  </span>
                </div>
              </div>
            )}
            
            <div className="power-location-stats">
              <div className="stat-item">
                <div className="stat-label">인버터</div>
                <div className="stat-value">
                  {powerLocation.inverter.working}/{powerLocation.inverter.total}
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-label">환경센서</div>
                <div className="stat-value">
                  {powerLocation.environmentSensor.working}/{powerLocation.environmentSensor.total}
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-label">접속반</div>
                <div className="stat-value">
                  {powerLocation.connectionBox.working}/{powerLocation.connectionBox.total}
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-label">통신</div>
                <div className="stat-value">
                  {powerLocation.communication.status === 'connected' ? '연결됨' : '연결안됨'}
                </div>
              </div>
            </div>
            </>
            ) : (
              <div className="power-location-expired-message">
                <p>이용기간이 끝났습니다</p>
                <small>제품 구매 메뉴에서 구독을 연장해 주세요</small>
              </div>
            )}
          </div>
          );
        })}
      </div>
      
      {/* 무한스크롤 로딩 */}
      {isLoading && (
        <div className="loading-row">
          <div className="loading-spinner"></div>
          <span>로딩 중...</span>
        </div>
      )}
      
      {/* 더 보기 버튼 */}
      {hasMore && !isLoading && onLoadMore && (
        <div className="load-more-section">
          <button onClick={onLoadMore} className="btn btn-secondary">
            더 보기
          </button>
        </div>
      )}
      
      {/* 더 이상 데이터가 없을 때 */}
      {!hasMore && powerLocations.length > 0 && (
        <div className="no-more-data">
          <span>모든 발전소를 불러왔습니다.</span>
        </div>
      )}
    </div>
  );
};

export default UserPowerLocationList;
